import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@vaiu/ui/card';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Slider } from '@vaiu/ui/slider';
import { Switch } from '@vaiu/ui/switch';
import { Badge } from '@vaiu/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@vaiu/ui/select';
import {
  Shield,
  Clock,
  User,
  AlertCircle,
  Phone,
  Mail,
  Hash,
  UserCheck,
  Lock,
} from 'lucide-react';

/** Matches @vaiu/cash-token-card `TokenCardToastFn` — keep in sync for injection. */
export type TokenConditioningToastFn = (props: {
  title: string;
  description?: string;
  variant?: 'default' | 'destructive';
  duration?: number;
}) => void;

export interface TokenConditions {
  timeLimit: number;
  recipientCriteria: {
    mustBeVerified?: boolean;
    restrictionType?:
      | 'none'
      | 'token'
      | 'phone'
      | 'email'
      | 'firstName'
      | 'lastName';
    tokenId?: string;
    phone?: string;
    email?: string;
    /** UI only until platform documents password-on-redemption under `validity` */
    password?: string;
    firstNameContains?: string;
    lastNameContains?: string;
  };
  location?: {
    text?: string;
    coordinates?: {
      lat: number;
      lng: number;
      radius: number;
      unit: 'km' | 'miles';
    };
  };
  escrow: boolean;
  escrowOptions?: {
    releaseType?: 'manual' | 'automatic' | 'delivery' | 'service';
    requireConfirmation?: boolean;
    autoReleaseAfterDays?: number;
    description?: string;
  };
}

/** Fields stored on token `payload` so redeemed escrowed cash tokens can be identified later. */
export type EscrowActivatePayload = {
  escrowRequired: true;
  escrowSettings: {
    releaseType: 'manual' | 'automatic' | 'delivery' | 'service';
    requireConfirmation: boolean;
    autoReleaseAfterDays?: number;
    description?: string;
  };
};

export function buildEscrowActivatePayload(
  conditions: TokenConditions | null | undefined
): EscrowActivatePayload | null {
  if (!conditions?.escrow) return null;
  return {
    escrowRequired: true,
    escrowSettings: {
      releaseType: conditions.escrowOptions?.releaseType || 'manual',
      requireConfirmation: Boolean(conditions.escrowOptions?.requireConfirmation),
      autoReleaseAfterDays: conditions.escrowOptions?.autoReleaseAfterDays,
      description: conditions.escrowOptions?.description,
    },
  };
}

/** Default conditioning state — keep in sync with `useState` initial value below. */
export const DEFAULT_TOKEN_CONDITIONS: TokenConditions = {
  timeLimit: 2,
  recipientCriteria: {
    restrictionType: 'none',
  },
  escrow: false,
};

export type ValidityRedemptionFields = {
  start: string;
  end: string;
  redeemer?: {
    firstName?: string;
    lastName?: string;
    username?: string;
    telephone?: string;
    email?: string;
  };
  location?: { lat: number; lng: number; rad: number };
};

export function buildValidityRedemptionExtensions(
  conditions: TokenConditions,
  timeWindow: { start: Date; end: Date }
): ValidityRedemptionFields {
  const rc = conditions.recipientCriteria;
  const redeemer: NonNullable<ValidityRedemptionFields['redeemer']> = {};

  const first = rc?.firstNameContains?.trim();
  const last = rc?.lastNameContains?.trim();
  const rt = rc?.restrictionType;
  if (rt === 'firstName' && first) {
    redeemer.firstName = first;
  }
  if (rt === 'lastName' && last) {
    redeemer.lastName = last;
  }

  switch (rt) {
    case 'phone':
      if (rc.phone?.trim()) redeemer.telephone = rc.phone.trim();
      break;
    case 'email':
      if (rc.email?.trim()) redeemer.email = rc.email.trim();
      break;
    case 'token':
      if (rc.tokenId?.trim()) redeemer.username = rc.tokenId.trim();
      break;
    default:
      break;
  }

  const coords = conditions.location?.coordinates;
  let location: ValidityRedemptionFields['location'];
  if (
    coords &&
    typeof coords.lat === 'number' &&
    typeof coords.lng === 'number' &&
    Number.isFinite(coords.lat) &&
    Number.isFinite(coords.lng)
  ) {
    const radius = coords.radius;
    const radMeters =
      coords.unit === 'miles'
        ? Math.round(radius * 1609.344)
        : Math.round(radius * 1000);
    location = {
      lat: coords.lat,
      lng: coords.lng,
      rad: radMeters > 0 ? radMeters : 1,
    };
  }

  return {
    start: timeWindow.start.toISOString(),
    end: timeWindow.end.toISOString(),
    ...(Object.keys(redeemer).length > 0 ? { redeemer } : {}),
    ...(location ? { location } : {}),
  };
}

export interface TokenConditioningProps {
  tokenKey?: string;
  tokenAmount: number;
  tokenCurrency: string;
  tokenType: 'cash' | 'invoice';
  /** Fired whenever internal condition state changes (parent uses this for create / activate). */
  onConditionsChange?: (conditions: TokenConditions) => void;
  /** After a successful PATCH when `tokenKey` is set (debounced save). */
  onConditionsSaved?: () => void;
  inline?: boolean;
  toast: TokenConditioningToastFn;
  /** Required when `tokenKey` is set — PATCH token conditions via platform API */
  updateTokenConditions?: (
    tokenKey: string,
    body: { validity: ValidityRedemptionFields }
  ) => Promise<unknown>;
}

const TokenConditioning: React.FC<TokenConditioningProps> = ({
  tokenKey = '',
  tokenAmount,
  tokenCurrency,
  tokenType,
  onConditionsChange,
  onConditionsSaved,
  inline = false,
  toast,
  updateTokenConditions,
}) => {
  const [conditions, setConditions] =
    useState<TokenConditions>(DEFAULT_TOKEN_CONDITIONS);

  /** Only PATCH after the user edits — opening the panel must not auto-save defaults. */
  const userHasEditedRef = useRef(false);
  const patchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const updateTokenConditionsRef = useRef(updateTokenConditions);
  updateTokenConditionsRef.current = updateTokenConditions;
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const onConditionsSavedRef = useRef(onConditionsSaved);
  onConditionsSavedRef.current = onConditionsSaved;

  const setConditionsFromUser = (
    updater: React.SetStateAction<TokenConditions>
  ) => {
    userHasEditedRef.current = true;
    setConditions(updater);
  };

  useEffect(() => {
    userHasEditedRef.current = false;
  }, [tokenKey]);

  /** useLayoutEffect so parent `tokenConditions` updates before paint — avoids Create submitting with null before useEffect ran. */
  useLayoutEffect(() => {
    onConditionsChange?.(conditions);
  }, [conditions, onConditionsChange]);

  /** Existing token: debounced PATCH — no separate Apply control. */
  useEffect(() => {
    if (!tokenKey || !updateTokenConditionsRef.current) return;
    if (!userHasEditedRef.current) return;

    if (patchDebounceRef.current) clearTimeout(patchDebounceRef.current);
    patchDebounceRef.current = setTimeout(async () => {
      try {
        const now = new Date();
        const endDate = new Date(
          now.getTime() + conditions.timeLimit * 24 * 60 * 60 * 1000
        );
        const validity = buildValidityRedemptionExtensions(conditions, {
          start: now,
          end: endDate,
        });
        await updateTokenConditionsRef.current!(tokenKey, { validity });
        onConditionsSavedRef.current?.();
      } catch (error) {
        console.error('Failed to save token conditions:', error);
        toastRef.current({
          title: 'Error',
          description:
            'Could not save conditions. The token may have already been revealed or expired.',
          variant: 'destructive',
        });
      }
    }, 450);

    return () => {
      if (patchDebounceRef.current) clearTimeout(patchDebounceRef.current);
    };
  }, [conditions, tokenKey]);

  const hasConditions =
    conditions.timeLimit !== 2 ||
    (conditions.recipientCriteria.restrictionType &&
      conditions.recipientCriteria.restrictionType !== 'none') ||
    !!conditions.recipientCriteria.password?.trim() ||
    !!conditions.location?.coordinates ||
    conditions.escrow;

  const content = (
    <>
      <div className="space-y-4">
        {!inline && tokenKey && (
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">
                  {tokenAmount} {tokenCurrency}
                </p>
                <p className="text-sm text-muted-foreground">
                  Token Key: {tokenKey}
                </p>
              </div>
              <Badge variant="outline">
                {tokenType === 'cash' ? 'Cash Token' : 'Invoice Token'}
              </Badge>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Valid for {conditions.timeLimit} days
          </Label>
          <Slider
            value={[conditions.timeLimit]}
            onValueChange={([value]) =>
              setConditionsFromUser((prev) => ({ ...prev, timeLimit: value }))
            }
            min={1}
            max={30}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>1 day</span>
            <span>30 days</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Token will expire after this period and funds will be refunded
          </p>
        </div>

        <div className="space-y-3">
          <Label className="flex items-center gap-2">
            <User className="w-4 h-4" />
            Recipient Requirements
          </Label>

          <div className="pl-6 space-y-3">
            <div className="space-y-2">
              <Label className="text-sm">Restrict to:</Label>
              <Select
                value={conditions.recipientCriteria.restrictionType || 'none'}
                onValueChange={(
                  value:
                    | 'none'
                    | 'token'
                    | 'phone'
                    | 'email'
                    | 'firstName'
                    | 'lastName'
                ) =>
                  setConditionsFromUser((prev) => {
                    const rc = prev.recipientCriteria;
                    return {
                      ...prev,
                      recipientCriteria: {
                        ...rc,
                        restrictionType: value,
                        tokenId: value === 'token' ? rc.tokenId : undefined,
                        phone: value === 'phone' ? rc.phone : undefined,
                        email: value === 'email' ? rc.email : undefined,
                        firstNameContains:
                          value === 'firstName' ? rc.firstNameContains : undefined,
                        lastNameContains:
                          value === 'lastName' ? rc.lastNameContains : undefined,
                      },
                    };
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Anyone can redeem" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <span className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Anyone can redeem
                    </span>
                  </SelectItem>
                  <SelectItem value="token">
                    <span className="flex items-center gap-2">
                      <Hash className="w-4 h-4" />
                      Specific user (username)
                    </span>
                  </SelectItem>
                  <SelectItem value="phone">
                    <span className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Phone number
                    </span>
                  </SelectItem>
                  <SelectItem value="email">
                    <span className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email address
                    </span>
                  </SelectItem>
                  <SelectItem value="firstName">
                    <span className="flex items-center gap-2">
                      <UserCheck className="w-4 h-4" />
                      First name (exact match)
                    </span>
                  </SelectItem>
                  <SelectItem value="lastName">
                    <span className="flex items-center gap-2">
                      <UserCheck className="w-4 h-4" />
                      Last name (exact match)
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {conditions.recipientCriteria.restrictionType === 'token' && (
              <Input
                placeholder="Redeemer username (must match user.username)"
                value={conditions.recipientCriteria.tokenId || ''}
                onChange={(e) =>
                  setConditionsFromUser((prev) => ({
                    ...prev,
                    recipientCriteria: {
                      ...prev.recipientCriteria,
                      tokenId: e.target.value.trim(),
                    },
                  }))
                }
                className="font-mono"
              />
            )}

            {conditions.recipientCriteria.restrictionType === 'phone' && (
              <Input
                type="tel"
                placeholder="Enter phone number (e.g., +1234567890)"
                value={conditions.recipientCriteria.phone || ''}
                onChange={(e) =>
                  setConditionsFromUser((prev) => ({
                    ...prev,
                    recipientCriteria: {
                      ...prev.recipientCriteria,
                      phone: e.target.value,
                    },
                  }))
                }
              />
            )}

            {conditions.recipientCriteria.restrictionType === 'email' && (
              <Input
                type="email"
                placeholder="Enter email address"
                value={conditions.recipientCriteria.email || ''}
                onChange={(e) =>
                  setConditionsFromUser((prev) => ({
                    ...prev,
                    recipientCriteria: {
                      ...prev.recipientCriteria,
                      email: e.target.value,
                    },
                  }))
                }
              />
            )}

            {conditions.recipientCriteria.restrictionType === 'firstName' && (
              <div className="space-y-2">
                <Label className="text-sm">First name</Label>
                <Input
                  placeholder="Exact first name (case-insensitive)"
                  value={conditions.recipientCriteria.firstNameContains || ''}
                  onChange={(e) =>
                    setConditionsFromUser((prev) => ({
                      ...prev,
                      recipientCriteria: {
                        ...prev.recipientCriteria,
                        firstNameContains: e.target.value,
                      },
                    }))
                  }
                />
              </div>
            )}

            {conditions.recipientCriteria.restrictionType === 'lastName' && (
              <div className="space-y-2">
                <Label className="text-sm">Last name</Label>
                <Input
                  placeholder="Exact last name (case-insensitive)"
                  value={conditions.recipientCriteria.lastNameContains || ''}
                  onChange={(e) =>
                    setConditionsFromUser((prev) => ({
                      ...prev,
                      recipientCriteria: {
                        ...prev.recipientCriteria,
                        lastNameContains: e.target.value,
                      },
                    }))
                  }
                />
              </div>
            )}
          </div>
        </div>

        <p className="text-xs text-muted-foreground pl-1">
          Verified-only and free-text location are deferred. Geo radius needs
          numeric lat/lng (e.g. map or geocoder)—not wired in this form yet.
        </p>

        <div
          className="rounded-md border border-dashed border-border/70 bg-muted/25 dark:bg-muted/10 px-3 py-4 space-y-5"
          aria-label="Coming soon options"
        >
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Coming soon
            </p>
            <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
              Redemption password below is preview only — not saved on activate
              or PATCH yet. Time window, &quot;Restrict to&quot;, and escrow
              are included when you create the token.
            </p>
          </div>

          <div className="space-y-2 pt-1 border-t border-border/50">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0">
              <Label className="text-sm flex items-center gap-2">
                <Lock className="w-4 h-4 shrink-0" />
                Redemption password
              </Label>
              <span className="text-[10px] text-muted-foreground">
                (coming soon)
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground leading-snug">
              Separate from recipient restrictions above. Not tied to the
              &quot;Restrict to&quot; menu.
            </p>
            <Input
              type="text"
              placeholder="Set a password for redemption"
              value={conditions.recipientCriteria.password || ''}
              onChange={(e) =>
                setConditionsFromUser((prev) => ({
                  ...prev,
                  recipientCriteria: {
                    ...prev.recipientCriteria,
                    password: e.target.value,
                  },
                }))
              }
            />
            <p className="text-[11px] text-muted-foreground leading-snug">
              Expected on the platform under{' '}
              <code className="text-[10px]">validity</code>, not{' '}
              <code className="text-[10px]">payload</code>.
            </p>
          </div>
        </div>

          <div className="space-y-4 pt-1">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-0.5 min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0">
                  <Label
                    htmlFor="escrow-switch"
                    className="flex items-center gap-2 text-sm"
                  >
                    <Shield className="w-4 h-4 shrink-0" />
                    Escrow protection
                  </Label>
                </div>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  Hold funds until release conditions are met.
                </p>
              </div>
              <Switch
                id="escrow-switch"
                className="shrink-0"
                checked={conditions.escrow}
                onCheckedChange={(checked) =>
                  setConditionsFromUser((prev) => ({ ...prev, escrow: checked }))
                }
              />
            </div>

          {conditions.escrow && (
            <div className="ml-0 sm:ml-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="escrow-release">Release conditions</Label>
                <Select
                  value={conditions.escrowOptions?.releaseType || 'manual'}
                  onValueChange={(
                    value: 'manual' | 'automatic' | 'delivery' | 'service'
                  ) =>
                    setConditionsFromUser((prev) => ({
                      ...prev,
                      escrowOptions: {
                        ...prev.escrowOptions,
                        releaseType: value,
                      },
                    }))
                  }
                >
                  <SelectTrigger id="escrow-release">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">
                      Manual release — you approve the release
                    </SelectItem>
                    <SelectItem value="automatic">
                      Automatic — released when conditions are met
                    </SelectItem>
                    <SelectItem value="delivery">
                      Delivery confirmation — released on delivery
                    </SelectItem>
                    <SelectItem value="service">
                      Service completion — released after service
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {conditions.escrowOptions?.releaseType === 'manual' && (
                <div className="space-y-2">
                  <Label htmlFor="auto-release">Auto-release after (days)</Label>
                  <div className="flex items-center gap-4">
                    <Slider
                      id="auto-release"
                      min={1}
                      max={30}
                      step={1}
                      value={[
                        conditions.escrowOptions?.autoReleaseAfterDays || 7,
                      ]}
                      onValueChange={([value]) =>
                        setConditionsFromUser((prev) => ({
                          ...prev,
                          escrowOptions: {
                            ...prev.escrowOptions,
                            autoReleaseAfterDays: value,
                          },
                        }))
                      }
                      className="flex-1"
                    />
                    <span className="w-12 text-sm font-medium">
                      {conditions.escrowOptions?.autoReleaseAfterDays || 7}d
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Funds auto-release if no dispute is raised
                  </p>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Switch
                  id="require-confirmation"
                  checked={
                    conditions.escrowOptions?.requireConfirmation || false
                  }
                  onCheckedChange={(checked) =>
                    setConditionsFromUser((prev) => ({
                      ...prev,
                      escrowOptions: {
                        ...prev.escrowOptions,
                        requireConfirmation: checked,
                      },
                    }))
                  }
                />
                <Label htmlFor="require-confirmation" className="text-sm">
                  Require recipient confirmation before release
                </Label>
              </div>

              <div className="space-y-2">
                <Label htmlFor="escrow-desc">Escrow description (optional)</Label>
                <Input
                  id="escrow-desc"
                  placeholder="e.g. Released after product delivery"
                  value={conditions.escrowOptions?.description || ''}
                  onChange={(e) =>
                    setConditionsFromUser((prev) => ({
                      ...prev,
                      escrowOptions: {
                        ...prev.escrowOptions,
                        description: e.target.value,
                      },
                    }))
                  }
                />
              </div>

              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="flex items-start gap-2">
                  <Lock className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div className="text-sm space-y-1">
                    <p className="font-medium text-amber-900 dark:text-amber-100">
                      Escrow
                    </p>
                    <p className="text-amber-700 dark:text-amber-200">
                      {(conditions.escrowOptions?.releaseType ?? 'manual') ===
                        'manual' &&
                        `Funds would be held until you manually approve release${
                          conditions.escrowOptions?.autoReleaseAfterDays
                            ? ` (auto-releases after ${conditions.escrowOptions.autoReleaseAfterDays} days)`
                            : ''
                        }.`}
                      {conditions.escrowOptions?.releaseType === 'automatic' &&
                        'Funds would be released automatically when all conditions are met.'}
                      {conditions.escrowOptions?.releaseType === 'delivery' &&
                        'Funds would be released upon delivery confirmation.'}
                      {conditions.escrowOptions?.releaseType === 'service' &&
                        'Funds would be released upon service completion confirmation.'}
                    </p>
                    {conditions.escrowOptions?.requireConfirmation && (
                      <p className="text-xs text-amber-600 dark:text-amber-300 mt-1">
                        Recipient would need to confirm receipt before release.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          </div>

        {hasConditions && (
          <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                Token Conditions Summary
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-200 mt-1">
                • Time validity: {conditions.timeLimit} days
                <br />
                {conditions.recipientCriteria.restrictionType &&
                  conditions.recipientCriteria.restrictionType !== 'none' && (
                    <>
                      • Redeemer restriction:{' '}
                      {conditions.recipientCriteria.restrictionType ===
                        'token' && 'username'}
                      {conditions.recipientCriteria.restrictionType ===
                        'phone' && 'phone'}
                      {conditions.recipientCriteria.restrictionType ===
                        'email' && 'email'}
                      {conditions.recipientCriteria.restrictionType ===
                        'firstName' && 'first name (exact)'}
                      {conditions.recipientCriteria.restrictionType ===
                        'lastName' && 'last name (exact)'}
                      <br />
                    </>
                  )}
                {conditions.location?.coordinates && (
                  <>• Geo radius on redeem<br /></>
                )}
                {!!conditions.recipientCriteria.password?.trim() && (
                  <>
                    • Redemption password{' '}
                    <span className="text-[11px] text-muted-foreground">
                      (coming soon)
                    </span>
                    <br />
                  </>
                )}
                {conditions.escrow && (
                  <>
                    • Escrow (manual release after redeem)
                    <br />
                  </>
                )}
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );

  if (inline) {
    return content;
  }

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Add Token Conditions
        </CardTitle>
        <CardDescription>
          Set requirements that must be met for this token to be redeemed
        </CardDescription>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
};

export default TokenConditioning;
